/* src/widgets/TestExportModal.jsx */
import { useEffect, useState } from 'react'
import {
  X,
  FileType,
  FileSpreadsheet,
  FileText,
  Download,
  LoaderCircle,
} from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import * as XLSX from 'xlsx'
import { renderAsync } from 'docx-preview'
import { getExportPreview, exportFile } from '@/api/kuizy'

const TYPES = {
  word: { icon: FileType, label: 'Word', ext: 'docx' },
  excel: { icon: FileSpreadsheet, label: 'Excel', ext: 'xlsx' },
  pdf: { icon: FileText, label: 'PDF', ext: 'pdf' },
}

export default function TestExportModal({ visible, cid, testId, onClose }) {
  const [mode, setMode] = useState('pdf')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [blobUrl, setBlobUrl] = useState('')
  const [excelRows, setExcelRows] = useState([])
  const [wordHtml, setWordHtml] = useState('')

  const loadPreview = async () => {
    setLoading(true)
    setError('')
    setBlobUrl('')
    setWordHtml('')
    setExcelRows([])
    try {
      const blob = await getExportPreview(cid, testId, mode)
      if (mode === 'pdf') {
        const url = URL.createObjectURL(blob)
        setBlobUrl(url)
      } else if (mode === 'excel') {
        const buf = await blob.arrayBuffer()
        const wb = XLSX.read(buf, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        setExcelRows(XLSX.utils.sheet_to_json(ws, { header: 1 }))
      } else {
        const div = document.createElement('div')
        await renderAsync(blob, div)
        setWordHtml(div.innerHTML)
      }
    } catch {
      setError('ไม่สามารถแสดงตัวอย่างได้')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (visible) loadPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode])

  if (!visible) return null

  const btnCls = m =>
    `flex-1 flex items-center justify-center gap-1 rounded-md px-3 py-2 text-sm border ${
      mode === m
        ? 'bg-primary text-white border-primary'
        : 'text-primary border-primary'
    }`

  const download = async () => {
    const blob = await exportFile(cid, testId, mode)
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${testId}.${TYPES[mode].ext}`
    a.click()
  }

  return (
    <AnimatePresence>
      <motion.div
        key="exportBack"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      >
        <motion.div
          key="exportCard"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="w-full h-full sm:h-[95vh] sm:w-[95%]  bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between px-6 py-4 border-b bg-info">
            <h3 className="text-base font-semibold">Export Test</h3>
            <button onClick={onClose} className="text-gray-600 hover:text-primary">
              <X size={20} />
            </button>
          </header>

          <div className="flex gap-2 p-4 border-b">
            {Object.keys(TYPES).map(t => {
              const Icon = TYPES[t].icon
              return (
                <button key={t} onClick={() => setMode(t)} className={btnCls(t)}>
                  <Icon size={16} /> {TYPES[t].label}
                </button>
              )
            })}
          </div>

          <div className="flex-1 overflow-auto p-4 bg-gray-50">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <LoaderCircle className="animate-spin text-primary" size={36} />
              </div>
            )}

            {error && !loading && (
              <p className="text-center text-red-600 text-sm">{error}</p>
            )}

            {!loading && !error && (
              <>
                {mode === 'pdf' && blobUrl && (
                  <iframe
                    title="pdf-preview"
                    src={blobUrl}
                    className="w-full h-full rounded-md border"
                  />
                )}

                {mode === 'excel' && excelRows.length > 0 && (
                  <div className="w-full overflow-auto">
                    <table className="min-w-full text-sm border border-gray-300">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          {excelRows[0].map((cell, idx) => (
                            <th
                              key={idx}
                              className="px-2 py-1 border border-gray-300 font-semibold text-gray-700"
                            >
                              {cell}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelRows.slice(1).map((row, r) => (
                          <tr
                            key={r}
                            className={r % 2 ? 'bg-white' : 'bg-gray-50'}
                          >
                            {row.map((cell, c) => (
                              <td
                                key={c}
                                className="px-2 py-1 border border-gray-300"
                              >
                                {cell ?? ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {mode === 'word' && (
                  <div
                    className="w-full overflow-auto prose max-w-none"
                    dangerouslySetInnerHTML={{
                      __html:
                        wordHtml ||
                        '<p class="text-center text-gray-500 text-sm py-10">ไม่รองรับตัวอย่าง Word</p>',
                    }}
                  />
                )}
              </>
            )}
          </div>

          <footer className="flex justify-end px-6 py-3 border-t bg-gray-50">
            <button
              onClick={download}
              className="flex items-center gap-1 px-4 py-2 bg-primary hover:bg-secondary text-white text-sm rounded-md"
            >
              <Download size={16} /> Export
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
